import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as IXrmTypes from 'model/CRM';
import * as FAM from 'model/FlexibleAttributeModel';
import { IGCData } from './dataSlice';
import { IGroupConfigurationStatus } from './statusSlice';

export interface ICompareGroupConfigurationData extends IGCData {
  status: IGroupConfigurationStatus;
}


const compSlice = createSlice({
  name: 'comp',
  initialState: {
    snapList: [] as IXrmTypes.bcbs_groupconfigurationsnapshot[],
    leftSnap: {} as ICompareGroupConfigurationData,
    rightSnap: {} as ICompareGroupConfigurationData,
    mergeSnap: {} as ICompareGroupConfigurationData,
    commentEdit: {} as FAM.IComment,
  },
  reducers: {
    loadSnapshotsData: (state, action: PayloadAction<IXrmTypes.bcbs_groupconfigurationsnapshot[]>) => {
      state.snapList = action.payload ?? [];
    },
    loadLeftSnap: (state, action: PayloadAction<ICompareGroupConfigurationData>) => {
      console.log("Left snapshot loaded", action.payload);
      state.leftSnap = { ...action.payload };
    },
    loadRightSnap: (state, action: PayloadAction<ICompareGroupConfigurationData>) => {
      // Right Snapshot
      // -------------------------------------
      state.rightSnap = { ...action.payload };
      console.log("right snapshot loaded", action.payload);
    },
    loadMergeSnap: (state, action: PayloadAction<ICompareGroupConfigurationData>) => {
      // Right Snapshot
      // -------------------------------------
      state.mergeSnap = { ...action.payload };
      console.log("merge snapshot loaded", action.payload);
    },
    addEditCommentToSnap: (state: any, action: PayloadAction<FAM.IComment>) => {
      const comments = state.rightSnap.json.comments.filter(
          (c: FAM.IComment) =>
          {   return  !(c.id === action.payload.id &&
              c.location.benefitId === action.payload.location.benefitId &&
              action.payload.location.planId === c.location.planId &&
              action.payload.location.feesAndPremiumId === c.location.feesAndPremiumId &&
              action.payload.location.claimsARId === c.location.claimsARId &&
              action.payload.location.stopLossId === c.location.stopLossId &&
              action.payload.location.tabId === c.location.tabId &&
              action.payload.location.fieldId === c.location.fieldId &&
              action.payload.location.sectionId === c.location.sectionId)
          }
      );   
      if (action.payload.comment.length > 0)
        state.rightSnap.json.comments = comments.concat(action.payload);
      else // allow removing comment
        state.rightSnap.json.comments = comments;
    },
    addEditComment: (state: any, action: PayloadAction<FAM.IComment>) => {
      state.commentEdit = { ...state.commentEdit, ...action.payload };
    },
    changeMergeSnap: (state: any, action: PayloadAction<any>) => {
      state.mergeSnap = { ...state.mergeSnap, ...action.payload };
    },
    updatePharmacyStatus: (state: any, action: PayloadAction<FAM.IPharmacyORE[]>) => {
      if (state.rightSnap.json.pharmacyORE)
        state.rightSnap.json.pharmacyORE = state.rightSnap.json.pharmacyORE?.filter((p: FAM.IPharmacyORE) => !action.payload.find(q => p.id === q.id)).concat(action.payload);
      else
        state.rightSnap.json.pharmacyORE = [...action.payload];
    },


    transformLeftSnapForCompare: (state, action: PayloadAction<{ leftSnapshot: ICompareGroupConfigurationData, rightSnapshot: ICompareGroupConfigurationData }>) => {
      // we have left snapshot and right snapshot, 
      // currently we are only targeting this update left snapshot benefit schema for tiers to match closely to right snapshot
      // lets cache right sides benefit

      // lets cache some lookups
      const lJson = action.payload.leftSnapshot.json;
      const rJson = action.payload.rightSnapshot.json;

      const rBenefitsLookupById = {} as Record<string, FAM.IBenefit>;
      const rBenefitsLookupByName = {} as Record<string, FAM.IBenefit>;

      rJson.benefits.forEach(rBenefit => {
        if(!rBenefit.deactivated) {
          rBenefitsLookupById[rBenefit.id] = rBenefit;
          rBenefitsLookupByName[rBenefit.name] = rBenefit;
        }
      });

      function updateFieldTiers(field: FAM.IFlexibleField, tierMapping: Record<string, FAM.ITierLevel>) {
        field.values.forEach(v => {
          if (v?.tierRef?.id) {
            const rightTier = tierMapping[v.tierRef.id];

            if (rightTier)
              v.tierRef.id = rightTier.id;
          }
        });
      }

      lJson.benefits.forEach(lBenefit => {
        if(!lBenefit.deactivated) {
          const rBenefit = rBenefitsLookupById[lBenefit.id] ?? rBenefitsLookupByName[lBenefit.name] ?? null;

          let lBenefitReconcile = false;
          // should we do anything?
          if (rBenefit) {

            // lBenefit.id = rBenefit.id;

            // tier refs are not the same or both are not null
            if (!lBenefitReconcile)
              if (lBenefit?.tierSetRef?.id !== rBenefit?.tierSetRef?.id)
                lBenefitReconcile = true;

            // something went wrong?
            if (!lBenefitReconcile)
              if (lBenefit?.tiers?.length !== rBenefit?.tiers?.length)
                lBenefitReconcile = true;

            // checking right tier index by index to match
            if (!lBenefitReconcile)
              rBenefit?.tiers?.forEach((eT, etIX) => {
                if (lBenefit?.tiers?.[etIX].id !== eT.id)
                  lBenefitReconcile = true;
              });
          }

          // Method A for reconciliation
          // this method recreates the entire tiered fields
          // -------------------------------------------------------------------------------
          // if (lBenefitReconcile) {
          //   // if tiers are not same length, match the left one as the right

          //   const tierChange: ITierChange = {
          //     tierChanges: reconcileTiers(lBenefit.tiers, rBenefit.tiers, false),
          //     tiers: structuredClone(rBenefit.tiers),
          //     tierSetRef: structuredClone(rBenefit.tierSetRef)
          //   };

          //   // Mapping Customizations
          //   // ----------------------------
          //   // To keep left tiers as is, and not balance to what right side has:
          //   // exclude everything new and make sure its all the ones that are coming from old(left)
          //   tierChange.tierChanges = tierChange.tierChanges
          //     .filter(q => q.oldTier && q.isNew !== true);

          //   tierChange.tierChanges.forEach(eT => {

          //     //  if during reconciliation, new tier was not created
          //     if (!eT.newTier)
          //       eT.newTier = structuredClone(eT.oldTier)

          //     // if during reconciliation, a tier is deleted, we ignore the delete request and keep it as is
          //     if (eT.isDelete) {
          //       eT.isDelete = false;
          //       eT.newTier = structuredClone(eT.oldTier);
          //     }
          //   });
          //   // ----------------------------

          //   // now tiers are same length, lets evaluate fields
          //   reconcileBenefit(lBenefit, tierChange, state.leftSnap.config, false);
          // }
          // -------------------------------------------------------------------------------

          // Method B, plain simple id replacement
          // this method maps the tiers and replaces only ids
          // -------------------------------------------------------------------------------
          if (lBenefitReconcile) {

            // tier mapping between left and right snapshot:
            const tierMapping = {} as Record<string, FAM.ITierLevel>;

            lBenefit.tiers.forEach(lTier => {

              // find a matching tier from right side:
              const rTier = rBenefit.tiers.find(q => q.bcbs_name === lTier.bcbs_name);

              if (rTier)
                tierMapping[lTier.id] = rTier;

            });

            lBenefit.fields.forEach(f => {
              updateFieldTiers(f, tierMapping);
            })

            lBenefit.nonStandards.forEach(ns => {
              ns.fields.forEach(f => {
                updateFieldTiers(f, tierMapping);
              });
            });

            // lBenefit.attributeSets.forEach(ast => {
            //   ast.fields.forEach(f => {
            //     updateFieldTiers(f, tierMapping);
            //   });
            // });

            lBenefit.tiers.forEach(t => {
              const rightTier = tierMapping[t.id];

              if (rightTier)
                t.id = rightTier.id;
            });

          }

          // // Testing/Verification Code
          // // ------------------------------
          // {
          //   const jsonStringify = JSON.stringify(lJson);
          //   const blob = new Blob([jsonStringify], { type: 'text/plan;charset=utf-8' });
          //   saveAs(blob, `left.json`, { autoBom: true });
          // }

          // {
          //   const jsonStringify = JSON.stringify(rJson);
          //   const blob = new Blob([jsonStringify], { type: 'text/plan;charset=utf-8' });
          //   saveAs(blob, `right.json`, { autoBom: true });
          // }

        }
      });
      state.leftSnap = {...action.payload.leftSnapshot, json: lJson};
    }
  },
})

// Action creators are generated for each case reducer function
export const {
  loadSnapshotsData,
  loadLeftSnap,
  loadRightSnap,
  addEditComment,
  addEditCommentToSnap,
  changeMergeSnap,
  loadMergeSnap,
  updatePharmacyStatus,
  transformLeftSnapForCompare,
} = compSlice.actions

export default compSlice.reducer